import { CloseOutlined, DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { OrderProductAlertConf, OrderProductAlertConfDoc } from '@gooduncles/gu-app-schema';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button, Popconfirm } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import withProduct from 'src/hoc/withProduct';
import { onCellValueChangedWithUpdate, onValueSetterWithValidation } from 'src/utils/ag-grid-util';
import { formatDate } from 'src/utils/date-util';
import {
  addProductToOrderProductAlertConf,
  createOrderProductAlertConf,
  deleteOrderProductAlertConf,
  observeOrderProductAlertConf,
  removeProductFromOrderProductAlertConf,
  updateOrderProductAlertConf,
} from 'src/utils/firebase/firebase-short-cut';

import AgCellToggleEditor from 'src/atomic-components/atoms/AgCell/AgCellToggleEditor';
import Divider from 'src/atomic-components/atoms/Divider/Divider';
import ProductListItem from 'src/atomic-components/atoms/ListItem/ProductListItem';
import AddProductToListModal from 'src/atomic-components/organisms/Modals/AddProductToListModal';

import classes from './AppConfig/AppConfig.module.scss';

const RemovableProductListItem = withProduct(ProductListItem);
const onCellValueChanged = onCellValueChangedWithUpdate<OrderProductAlertConfDoc>('발주서');
const OrderProductAlertConfDocPath = 'orderProductAlertConf';
const initialConfig: OrderProductAlertConf = {
  alertAt: '23:00',
  slackChannel: '2-주문입력',
  header: '상품 알림',
  footer: '확인하세요.',
  productIds: [],
  isActive: false,
  latestAlertAt: null,
};
const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const OrderProductAlert: FC = () => {
  const [configList, setConfigList] = useState<OrderProductAlertConfDoc[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string>();
  const selectedConfig = useMemo(
    () => configList.find((config) => config._id === selectedConfigId) ?? null,
    [configList, selectedConfigId]
  );

  const columnDefs: ColDef<OrderProductAlertConfDoc>[] = useMemo(
    () => [
      {
        field: '_id',
        hide: true,
      },
      {
        field: 'alertAt',
        headerName: '알림 시간',
        width: 110,
        editable: true,
        valueSetter: (params) =>
          onValueSetterWithValidation(params, ['_id'], (value) => {
            const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
            return timeRegex.test(value);
          }),
        onCellValueChanged: (params) => onCellValueChanged(params, OrderProductAlertConfDocPath, params.data._id),
      },
      {
        field: 'slackChannel',
        headerName: '슬랙 채널',
        width: 140,
        editable: true,
        valueSetter: (params) => onValueSetterWithValidation(params, ['_id']),
        onCellValueChanged: (params) => onCellValueChanged(params, OrderProductAlertConfDocPath, params.data._id),
      },
      {
        field: 'header',
        headerName: '머리말',
        width: 240,
        editable: true,
        valueSetter: (params) =>
          onValueSetterWithValidation(params, ['_id'], undefined, { nullable: false, removable: true }),
        onCellValueChanged: (params) =>
          onCellValueChanged(params, OrderProductAlertConfDocPath, params.data._id, 'header', true, false, ''),
      },
      {
        field: 'footer',
        headerName: '꼬리말',
        width: 240,
        editable: true,
        valueSetter: (params) =>
          onValueSetterWithValidation(params, ['_id'], undefined, { nullable: false, removable: true }),
        onCellValueChanged: (params) =>
          onCellValueChanged(params, OrderProductAlertConfDocPath, params.data._id, 'footer', true, false, ''),
      },
      {
        field: 'productIds',
        headerName: '상품 수',
        width: 100,
        valueGetter: (params) => {
          if (!params.data) return 0;
          return params.data.productIds.length;
        },
      },
      {
        field: 'isActive',
        headerName: '활성화 여부',
        width: 120,
        cellRenderer: (params: any) => (
          <AgCellToggleEditor
            id={params.data._id}
            value={params.data.isActive}
            field='isActive'
            onChange={updateOrderProductAlertConf}
          />
        ),
      },
      {
        field: 'latestAlertAt',
        headerName: '최근 알림 시간',
        width: 160,
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDate(params.value, 'L.dd(EEEEEE) HH:mm:ss');
        },
      },
      {
        field: '_id',
        headerName: '삭제',
        cellRenderer: (params: any) => (
          <Popconfirm title='삭제하시겠습니까?' onConfirm={() => deleteOrderProductAlertConf(params.data._id)}>
            <Button type='text' icon={<DeleteOutlined style={{ color: 'var(--red400)' }} />} />
          </Popconfirm>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    const subscription = observeOrderProductAlertConf().subscribe((snapshot) => {
      setConfigList(snapshot);
    });
    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className={`${classes.appConfigContainer} flexColumn height100`}>
      <h2>상품 알림 설정</h2>
      <div className='flexRow flexSpaceBetween flexAlignCenter'>
        <Button type='primary' onClick={() => createOrderProductAlertConf(initialConfig)}>
          새로운 설정 추가
        </Button>
        <span className='red400'>※ 알림 조건: 현재 시간이 알림 시간과 같거나 이후 & 오늘 알림을 보내지 않았음</span>
      </div>
      <div className='flexRow flex1'>
        <div className='ag-theme-alpine flex1'>
          <AgGridReact
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowData={configList}
            rowSelection='single'
            // 행 선택을 켜놓더라도 개별 셀의 내용을 복사할 수 있도록한다.
            enableCellTextSelection={true}
            onSelectionChanged={(e) => {
              setSelectedConfigId(e.api.getSelectedRows()[0]?._id);
            }}
          />
        </div>
        <div className='borderedScrollBox' style={{ width: 320 }}>
          {selectedConfig && (
            <div>
              <b>{selectedConfig.header}</b>
              <Divider />
              <AddProductToListModal
                currentProductIds={selectedConfig.productIds}
                openButton={
                  <Button
                    type='text'
                    block
                    style={{ justifyContent: 'flex-start', display: 'flex', alignItems: 'center', height: 42 }}
                    icon={<PlusCircleOutlined style={{ color: 'var(--blue400)' }} />}>
                    상품 추가
                  </Button>
                }
                onAdd={(productId) => addProductToOrderProductAlertConf(selectedConfig._id, productId)}
                onRemove={(productId) => removeProductFromOrderProductAlertConf(selectedConfig._id, productId)}
              />
              {selectedConfig.productIds.map((productId) => (
                <RemovableProductListItem
                  key={productId}
                  productId={productId}
                  showCode={true}
                  rightElement={
                    <Button
                      type='text'
                      icon={<CloseOutlined style={{ color: 'var(--red400)' }} />}
                      onClick={() => removeProductFromOrderProductAlertConf(selectedConfig._id, productId)}
                    />
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderProductAlert;
